import Button from 'tio-ui/components/button';
import { dropTask } from 'ember-concurrency';
import { eq } from 'tio-ui/utilities';
import { array, fn, hash } from '@ember/helper';
import Checkbox from 'tio-ui/components/checkbox';
import Input from 'tio-ui/components/input';
import RadioGroup from 'tio-ui/components/radio-group';
import Form from 'tio-ui/components/form';
import Modal from 'tio-ui/components/modal';
import Footer from 'tio-ui/components/footer';
import Select from 'tio-ui/components/select';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { stateOptions } from 'tio-common/components/strategy-finder/strategy-finder-form';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import { VStack, Section, Header } from 'tio-ui/components/layout';
import * as v from 'valibot';
import AlertBanner from 'tio-ui/components/alert-banner';
import Component from '@glimmer/component';
import DefinitionList from 'tio-common/components/ui/definition-list';
import errorsForValibotField from 'tio-common/helpers/errors-for-valibot-field';
import inputmask from 'tio-common/modifiers/inputmask';
import safeFormatDate from 'tio-common/helpers/safe-format-date';
import SignaturePad from 'tio-common/components/signature-pad';
import type { FormResult } from 'tio-ui/components/form';
import type { IntlService } from 'ember-intl';
import type { NotificationsService } from 'tio-ui/components/notifications';
import type { PointGroup } from 'signature_pad';
import type Owner from '@ember/owner';
import type RouterService from '@ember/routing/router-service';
// This is for types. Anything we have custom may show an error.
import type StoreService from 'ember-data/store';

export type EmployerFormInfo = {
  borrowerName: string;
  borrowerSignDate: string;
  employerSignDate: string | null;
  employerName: string;
  employerFein: string;
  employerWebsite: string;
  employerStreetAddress: string;
  employerApartment: string;
  employerCity: string;
  employerState: string;
  employerPostalCode: string;
  employmentStartDate: string;
  employmentEndDate: string | null;
  employmentContinues: boolean;
  employmentHours: string;
  employmentStatus: string;
  employerContactName: string;
  employerContactTitle: string;
  employerContactPhoneNumber: string;
  employerContactEmail: string;
  status: string;
};

interface S {
  Args: {
    model: { employerFormInfo: EmployerFormInfo; approvalId: string };
  };
  Element: HTMLDivElement;
}

class PslfEmployerNonPartnerSign extends Component<S> {
  @service declare router: RouterService;
  @service declare store: StoreService;
  @service declare intl: IntlService;
  @service declare notifications: NotificationsService;

  @tracked signaturePoints: PointGroup[] = [];
  @tracked signatureImage = '';
  @tracked showConfirmationModal = false;
  @tracked modalStep: 'confirmation' | 'signature' = 'confirmation';
  @tracked hasSubmitted = false;
  @tracked isAlreadySigned = false;
  @tracked errors: Record<string, string[]> = {};
  @tracked signatureErrors: string[] = [];
  @tracked formData: Partial<EmployerFormInfo> = {};
  @tracked showForm = false;

  constructor(owner: Owner, args: S['Args']) {
    super(owner, args);

    this.formData = {
      employerName: args.model.employerFormInfo.employerName,
      employerFein: '',
      employerWebsite: args.model.employerFormInfo.employerWebsite || '',
      employerStreetAddress: '',
      employerApartment: '',
      employerCity: '',
      employerState: '',
      employerPostalCode: '',
      employmentStartDate: args.model.employerFormInfo.employmentStartDate,
      employmentEndDate: args.model.employerFormInfo.employmentEndDate || '',
      employmentContinues: args.model.employerFormInfo.employmentContinues,
      employmentHours: String(args.model.employerFormInfo.employmentHours || ''),
      employmentStatus: args.model.employerFormInfo.employmentStatus,
      employerContactName: args.model.employerFormInfo.employerContactName,
      employerContactTitle: args.model.employerFormInfo.employerContactTitle,
      employerContactPhoneNumber: this.formatPhoneForMask(
        args.model.employerFormInfo.employerContactPhoneNumber
      ),
      employerContactEmail: args.model.employerFormInfo.employerContactEmail,
    };

    this.isAlreadySigned =
      args.model.employerFormInfo.status !== 'borrower_signed' &&
      !!args.model.employerFormInfo.employerSignDate;
  }

  formatPhoneForMask = (phone: string | undefined) => {
    if (!phone) return '';
    const digits = phone.replace(/\D/g, '');
    let tenDigits: string;
    if (digits.length === 11 && digits.startsWith('1')) {
      tenDigits = digits.slice(1);
    } else if (digits.length === 10) {
      tenDigits = digits;
    } else {
      return phone;
    }
    // Format to match the inputmask output: +1 (XXX) XXX-XXXX
    return `+1 (${tenDigits.slice(0, 3)}) ${tenDigits.slice(3, 6)}-${tenDigits.slice(6)}`;
  };

  get states() {
    return stateOptions.map((state) => ({
      label: state.name,
      key: state.abbr,
    }));
  }

  get borrowerName() {
    return this.args.model.employerFormInfo.borrowerName || '';
  }

  get borrowerSignDate() {
    return this.args.model.employerFormInfo.borrowerSignDate || '';
  }

  get employmentContinues() {
    return this.formData.employmentContinues as boolean;
  }

  get employmentEndDate() {
    return this.formData.employmentEndDate || '';
  }

  get formSchema() {
    return v.object({
      employerName: v.pipe(
        v.string('Company name is required'),
        v.nonEmpty('Company name is required')
      ),
      employerFein: v.pipe(
        v.string('FEIN is required'),
        v.nonEmpty('FEIN is required'),
        v.regex(/^\d{2}-?\d{7}$/, 'FEIN must be 9 digits')
      ),
      employerWebsite: v.optional(v.nullable(v.string(), ''), ''),
      employerStreetAddress: v.pipe(
        v.string('Street address is required'),
        v.nonEmpty('Street address is required')
      ),
      employerApartment: v.optional(v.string(), ''),
      employerCity: v.pipe(v.string('City is required'), v.nonEmpty('City is required')),
      employerState: v.pipe(v.string('State is required'), v.nonEmpty('State is required')),
      employerPostalCode: v.pipe(
        v.string('Postal code is required'),
        v.nonEmpty('Postal code is required'),
        v.regex(/^\d{5}$/, 'Please enter a valid 5-digit ZIP code.')
      ),
      employmentStartDate: v.pipe(
        v.string('Start date is required'),
        v.nonEmpty('Start date is required'),
        v.isoDate('Please enter a valid date')
      ),
      employmentEndDate: this.employmentContinues
        ? v.optional(v.string())
        : v.pipe(
            v.string('End date is required'),
            v.nonEmpty('End date is required'),
            v.isoDate('Please enter a valid date')
          ),
      employmentHours: v.pipe(
        v.string('Hours per week is required'),
        v.transform((input) => Number(input)),
        v.number('Hours per week is required'),
        v.minValue(1, 'Hours must be at least 1.')
      ),
      employmentStatus: v.pipe(
        v.string('Employment status is required'),
        v.nonEmpty('Employment status is required')
      ),
      employerContactName: v.pipe(v.string('Name is required'), v.nonEmpty('Name is required')),
      employerContactTitle: v.pipe(v.string('Title is required'), v.nonEmpty('Title is required')),
      employerContactPhoneNumber: v.pipe(
        v.string('Phone number is required'),
        v.nonEmpty('Phone number is required'),
        v.check((input) => {
          const digits = input.replace(/\D/g, '');
          // Mask includes +1, so complete number is always 11 digits starting with 1
          return digits.length === 11 && digits.startsWith('1');
        }, 'Please enter a valid US phone number.')
      ),
      employerContactEmail: v.pipe(
        v.string('Email is required'),
        v.nonEmpty('Email is required'),
        v.email('Please enter a valid email address.')
      ),
    });
  }

  updateFormField = (field: keyof EmployerFormInfo, value: unknown) => {
    this.formData = { ...this.formData, [field]: value };
    if (this.hasSubmitted) {
      this.runValidation();
    }
  };

  runValidation = () => {
    const validationResult = v.safeParse(this.formSchema, this.formData);

    if (validationResult.success) {
      this.errors = {};
    } else {
      const validationErrors: Record<string, string[]> = {};
      for (const issue of validationResult.issues) {
        const path = issue.path?.[0]?.key as string;
        if (path) {
          if (!validationErrors[path]) {
            validationErrors[path] = [];
          }
          validationErrors[path].push(issue.message);
        }
      }
      this.errors = validationErrors;
    }
  };

  onFormSubmit = (_formResultData: FormResult<Partial<EmployerFormInfo>>) => {
    this.runValidation();
    this.hasSubmitted = true;
    const errorKeys = Object.keys(this.errors);
    if (errorKeys.length === 0) {
      // Form is valid, proceed with modal
      this.showConfirmationModal = true;
      this.modalStep = 'confirmation';
    } else {
      const firstErrorKey = errorKeys[0];
      if (firstErrorKey) {
        const element = document.querySelector(`[name="${firstErrorKey}"]`) as HTMLElement;
        element?.focus();
      }
    }
  };

  handleStateChange = (selectedKey: string | null) => {
    this.formData = { ...this.formData, employerState: selectedKey ?? '' };
    // Clear or set employerState errors since the Select doesn't trigger native form change events
    const { employerState: _removed, ...rest } = this.errors;
    if (selectedKey) {
      this.errors = rest;
    } else if (this.hasSubmitted) {
      this.errors = { ...rest, employerState: ['State is required'] };
    }
  };

  toggleEmploymentContinues = (value: boolean) => {
    this.formData = {
      ...this.formData,
      employmentContinues: value,
      employmentEndDate: value ? '' : this.formData.employmentEndDate,
    };
    if (this.hasSubmitted) {
      this.runValidation();
    }
  };

  toggleShowForm = () => {
    this.showForm = !this.showForm;
  };

  onSignatureUpdate = (points: PointGroup[], signatureUrl: string) => {
    this.signaturePoints = points;
    this.signatureImage = signatureUrl;

    if (signatureUrl) {
      this.signatureErrors = [];
    }
  };

  clearSignature = () => {
    this.signaturePoints = [];
    this.signatureImage = '';
  };

  goToSignatureStep = () => {
    this.modalStep = 'signature';
  };

  goBackToConfirmation = () => {
    this.modalStep = 'confirmation';
  };

  toggleConfirmationModal = () => {
    this.showConfirmationModal = !this.showConfirmationModal;
    if (!this.showConfirmationModal) {
      this.modalStep = 'confirmation';
      this.clearSignature();
    }
  };

  submitTask = dropTask(async () => {
    if (this.modalStep === 'confirmation') {
      this.goToSignatureStep();
      return;
    }

    if (!this.signatureImage) {
      this.signatureErrors = ['Signature is required.'];
      return;
    }
    const hasSignatureComplexity = this.signaturePoints.some((group) => group.points.length > 5); // anything less than 5 is a dot
    if (!hasSignatureComplexity) {
      this.signatureErrors = ['Please provide a more detailed signature.'];
      return;
    }

    this.signatureErrors = [];

    try {
      const employerFields = {
        ...this.formData,
        employerContactPhoneNumber:
          this.formData.employerContactPhoneNumber?.replace(/[\(\)\-\s]/g, '') ?? '',
      };

      await this.store
        .adapterFor('pslf-form')
        // @ts-expect-error - Legacy adapter method, will be migrated
        .signNonPartnerEmployer(this.args.model.approvalId, this.signatureImage, employerFields);

      this.isAlreadySigned = true;

      this.toggleConfirmationModal();
    } catch (error) {
      console.error('Failed to submit form:', error);
      this.toggleConfirmationModal();
      this.notifications.add('Failed to submit employer signature.', {
        appearance: 'error',
      });
    }
  });

  <template>
    <VStack class="max-w-3xl mx-auto">
      {{#if this.isAlreadySigned}}
        <Section>
          <AlertBanner @intent="info" data-test-already-signed-alert>
            {{t "pslf.form_no_longer_awaiting_approval"}}
          </AlertBanner>
        </Section>
      {{else}}
        <Header data-test-sign-header>{{t "pslf.employer_sign_page_title"}}
          {{this.borrowerName}}</Header>
        {{#if this.showForm}}
          <Form @data={{this.formData}} @onSubmit={{this.onFormSubmit}} data-test-sign-form>
            <VStack>
              <Input
                @name="employerName"
                @label={{t "pslf.approver_email_landing_page.what_is_org_name"}}
                @value={{this.formData.employerName}}
                @onChange={{fn this.updateFormField "employerName"}}
                @isRequired={{true}}
                @errors={{errorsForValibotField
                  "employerName"
                  schemaErrors=this.errors
                  hasSubmitted=this.hasSubmitted
                }}
                data-test-employer-name
              />
              <Input
                @name="employerFein"
                @label={{t "pslf.employer_workflow.fein_header"}}
                @value={{this.formData.employerFein}}
                @onChange={{fn this.updateFormField "employerFein"}}
                @isRequired={{true}}
                @errors={{errorsForValibotField
                  "employerFein"
                  schemaErrors=this.errors
                  hasSubmitted=this.hasSubmitted
                }}
                {{inputmask mask="99-9999999"}}
                data-test-employer-fein
              />
              <Input
                @name="employerWebsite"
                @label={{t "pslf.employer_workflow.company_website"}}
                @value={{this.formData.employerWebsite}}
                @onChange={{fn this.updateFormField "employerWebsite"}}
                @errors={{errorsForValibotField
                  "employerWebsite"
                  schemaErrors=this.errors
                  hasSubmitted=this.hasSubmitted
                }}
                data-test-employer-website
              />
              <Input
                @name="employerStreetAddress"
                @label={{t "pslf.employer_workflow.address_line_1"}}
                @value={{this.formData.employerStreetAddress}}
                @onChange={{fn this.updateFormField "employerStreetAddress"}}
                @isRequired={{true}}
                @errors={{errorsForValibotField
                  "employerStreetAddress"
                  schemaErrors=this.errors
                  hasSubmitted=this.hasSubmitted
                }}
                data-test-employer-street-address
              />
              <Input
                @name="employerApartment"
                @label={{t "pslf.employer_workflow.address_line_2"}}
                @value={{this.formData.employerApartment}}
                @onChange={{fn this.updateFormField "employerApartment"}}
                @errors={{errorsForValibotField
                  "employerApartment"
                  schemaErrors=this.errors
                  hasSubmitted=this.hasSubmitted
                }}
                data-test-employer-apartment
              />
              <Input
                @name="employerCity"
                @label={{t "pslf.employer_workflow.city"}}
                @value={{this.formData.employerCity}}
                @onChange={{fn this.updateFormField "employerCity"}}
                @isRequired={{true}}
                @errors={{errorsForValibotField
                  "employerCity"
                  schemaErrors=this.errors
                  hasSubmitted=this.hasSubmitted
                }}
                data-test-employer-city
              />
              <Select
                @name="employerState"
                @label={{t "pslf.employer_workflow.state"}}
                @items={{this.states}}
                @selected={{this.formData.employerState}}
                @onChange={{this.handleStateChange}}
                @searchable={{true}}
                @clearable={{true}}
                @errors={{errorsForValibotField
                  "employerState"
                  schemaErrors=this.errors
                  hasSubmitted=this.hasSubmitted
                }}
                data-test-employer-state
              />
              <Input
                @name="employerPostalCode"
                @label={{t "pslf.employer_workflow.zip_code"}}
                @value={{this.formData.employerPostalCode}}
                @onChange={{fn this.updateFormField "employerPostalCode"}}
                @isRequired={{true}}
                @errors={{errorsForValibotField
                  "employerPostalCode"
                  schemaErrors=this.errors
                  hasSubmitted=this.hasSubmitted
                }}
                {{inputmask mask="99999"}}
                data-test-employer-postal-code
              />
              <Input
                @name="employmentStartDate"
                @type="date"
                @label={{t "pslf.workflow.start_date"}}
                @value={{this.formData.employmentStartDate}}
                @onChange={{fn this.updateFormField "employmentStartDate"}}
                @isRequired={{true}}
                @errors={{errorsForValibotField
                  "employmentStartDate"
                  schemaErrors=this.errors
                  hasSubmitted=this.hasSubmitted
                }}
                data-test-employment-start-date
              />
              <Checkbox
                @label={{t "pslf.employer_workflow.still_employed"}}
                @checked={{this.formData.employmentContinues}}
                @onChange={{this.toggleEmploymentContinues}}
                data-test-employment-continues
              />
              {{#unless this.employmentContinues}}
                <Input
                  @name="employmentEndDate"
                  @type="date"
                  @label={{t "pslf.workflow.end_date"}}
                  @value={{this.employmentEndDate}}
                  @onChange={{fn this.updateFormField "employmentEndDate"}}
                  @isRequired={{true}}
                  @errors={{errorsForValibotField
                    "employmentEndDate"
                    schemaErrors=this.errors
                    hasSubmitted=this.hasSubmitted
                  }}
                  data-test-employment-end-date
                />
              {{/unless}}
              <Input
                @name="employmentHours"
                @label={{t "pslf.employer_workflow.average_hours_per_week"}}
                @value={{this.formData.employmentHours}}
                @onChange={{fn this.updateFormField "employmentHours"}}
                @isRequired={{true}}
                @errors={{errorsForValibotField
                  "employmentHours"
                  schemaErrors=this.errors
                  hasSubmitted=this.hasSubmitted
                }}
                {{inputmask mask="99"}}
                data-test-employment-hours
              />
              <RadioGroup
                @label={{t "pslf.employer_workflow.employment_type"}}
                @value={{this.formData.employmentStatus}}
                @onChange={{fn this.updateFormField "employmentStatus"}}
                @errors={{errorsForValibotField
                  "employmentStatus"
                  schemaErrors=this.errors
                  hasSubmitted=this.hasSubmitted
                }}
                @options={{array
                  (hash key="fullTime" label=(t "common.full_time") dataTestId="radio-full-time")
                  (hash key="partTime" label=(t "common.part_time") dataTestId="radio-part-time")
                }}
              />
              <Input
                @name="employerContactName"
                @type="text"
                @label={{t "pslf.employer_workflow.your_name"}}
                @value={{this.formData.employerContactName}}
                @onChange={{fn this.updateFormField "employerContactName"}}
                @isRequired={{true}}
                @errors={{errorsForValibotField
                  "employerContactName"
                  schemaErrors=this.errors
                  hasSubmitted=this.hasSubmitted
                }}
                data-test-employer-contact-name
              />
              <Input
                @name="employerContactTitle"
                @type="text"
                @label={{t "pslf.employer_workflow.your_title"}}
                @value={{this.formData.employerContactTitle}}
                @onChange={{fn this.updateFormField "employerContactTitle"}}
                @isRequired={{true}}
                @errors={{errorsForValibotField
                  "employerContactTitle"
                  schemaErrors=this.errors
                  hasSubmitted=this.hasSubmitted
                }}
                data-test-employer-contact-title
              />
              <Input
                @name="employerContactPhoneNumber"
                @type="tel"
                @label={{t "pslf.employer_workflow.your_phone_number"}}
                @value={{this.formData.employerContactPhoneNumber}}
                @onChange={{fn this.updateFormField "employerContactPhoneNumber"}}
                @isRequired={{true}}
                @errors={{errorsForValibotField
                  "employerContactPhoneNumber"
                  schemaErrors=this.errors
                  hasSubmitted=this.hasSubmitted
                }}
                {{inputmask mask="+1 (999) 999-9999"}}
                data-test-employer-contact-phone
              />
              <Input
                @name="employerContactEmail"
                @type="email"
                @label={{t "pslf.employer_workflow.your_email_address"}}
                @value={{this.formData.employerContactEmail}}
                @onChange={{fn this.updateFormField "employerContactEmail"}}
                @isRequired={{true}}
                @errors={{errorsForValibotField
                  "employerContactEmail"
                  schemaErrors=this.errors
                  hasSubmitted=this.hasSubmitted
                }}
                data-test-employer-contact-email
              />
              <div class="text-center">
                <Button
                  @intent="primary"
                  @type="submit"
                  @isRunning={{this.submitTask.isRunning}}
                  data-test-continue-btn
                >
                  {{t "common.continue"}}
                </Button>
              </div>
            </VStack>
          </Form>
        {{else}}
          <Section>
            <:header>{{t "pslf.default_full"}}</:header>
            <:body>
              <VStack class="space-y-6">
                <p>{{t
                    "pslf.approver_email_landing_page.sent_to_by"
                    email=this.formData.employerContactEmail
                    borrowerName=this.borrowerName
                    dateSent=(safeFormatDate
                      this.borrowerSignDate month="long" day="2-digit" year="numeric" timeZone="UTC"
                    )
                  }}</p>
                <p>{{t
                    "pslf.approver_email_landing_page.explanation"
                    borrowerName=this.borrowerName
                  }}</p>
                <Section>
                  <:body>
                    <p class="text-sm tio-fg-muted mb-1">{{t
                        "pslf.approver_email_landing_page.employer_label"
                      }}</p>
                    <p
                      class="tio-text-xl-semibold text-midnight"
                      data-test-company-name
                    >{{this.formData.employerName}}</p>
                    <div class="mt-3 flex flex-wrap gap-x-6 gap-y-2 text-sm">
                      <div>
                        <span class="tio-fg-muted">{{t
                            "pslf.start_new.employment_start_date"
                          }}:</span>
                        <span class="font-medium ml-1" data-test-employment-start-date>{{#if
                            this.formData.employmentStartDate
                          }}
                            {{safeFormatDate
                              this.formData.employmentStartDate
                              month="long"
                              day="2-digit"
                              year="numeric"
                              timeZone="UTC"
                            }}
                          {{else}}
                            {{t "pslf.table.tbd"}}
                          {{/if}}</span>
                      </div>
                      <div>
                        <span class="tio-fg-muted">{{t
                            "pslf.start_new.employment_end_date"
                          }}:</span>
                        <span class="font-medium ml-1" data-test-employment-end-date>
                          {{#if this.employmentContinues}}
                            {{t "pslf.table.present"}}
                          {{else if this.employmentEndDate}}
                            {{safeFormatDate
                              this.employmentEndDate
                              month="long"
                              day="2-digit"
                              year="numeric"
                              timeZone="UTC"
                            }}
                          {{else}}
                            {{t "pslf.table.tbd"}}
                          {{/if}}
                        </span>
                      </div>
                    </div>
                  </:body>
                </Section>
                <p>{{t
                    "pslf.approver_email_landing_page.directive"
                    companyName=this.formData.employerName
                    borrowerName=this.borrowerName
                  }}</p>
                <p>{{t "pslf.approver_email_landing_page.if_in_error" htmlSafe=true}}</p>
                <div class="text-center">
                  <Button
                    @intent="primary"
                    {{on "click" this.toggleShowForm}}
                    data-test-sign-form-toggle
                  >
                    {{t "pslf.approver_email_landing_page.view_form"}}
                  </Button>
                </div>
              </VStack>
            </:body>
          </Section>
        {{/if}}

        <Modal
          @isOpen={{this.showConfirmationModal}}
          @onClose={{this.toggleConfirmationModal}}
          @size="xl"
        >
          <:header>
            <span class="text-midnight font-semibold ml-4 mt-4">
              {{#if (eq this.modalStep "confirmation")}}
                {{t "pslf.employer_workflow.is_info_correct"}}
              {{else}}
                {{t "pslf.employer_sign_page_title"}}
              {{/if}}
            </span>
          </:header>
          <:body>
            <VStack data-test-sign-modal-body>
              {{#if (eq this.modalStep "confirmation")}}
                <p>{{t "pslf.employer_workflow.please_review"}}</p>
                <DefinitionList class="px-0" as |options|>
                  <div class={{options.wrappingClass}}>
                    <options.dt class="font-semibold mr-2">{{t
                        "pslf.workflow.employer_name"
                      }}:</options.dt>
                    <options.dd>{{this.formData.employerName}}</options.dd>
                  </div>
                  <div class={{options.wrappingClass}}>
                    <options.dt class="font-semibold mr-2">{{t "pslf.forms.fein"}}</options.dt>
                    <options.dd>{{this.formData.employerFein}}</options.dd>
                  </div>
                  <div class={{options.wrappingClass}}>
                    <options.dt class="font-semibold mr-2">{{t
                        "pslf.employer_workflow.company_website"
                      }}:</options.dt>
                    <options.dd>{{this.formData.employerWebsite}}</options.dd>
                  </div>
                  <div class={{options.wrappingClass}}>
                    <options.dt class="font-semibold mr-2">{{t
                        "pslf.employer_workflow.address_line_1"
                      }}:</options.dt>
                    <options.dd>{{this.formData.employerStreetAddress}}</options.dd>
                  </div>
                  {{#if this.formData.employerApartment}}
                    <div class={{options.wrappingClass}}>
                      <options.dt class="font-semibold mr-2">{{t
                          "pslf.employer_workflow.address_line_2"
                        }}:</options.dt>
                      <options.dd>{{this.formData.employerApartment}}</options.dd>
                    </div>
                  {{/if}}
                  <div class={{options.wrappingClass}}>
                    <options.dt class="font-semibold mr-2">{{t
                        "pslf.employer_workflow.city"
                      }}:</options.dt>
                    <options.dd>{{this.formData.employerCity}}</options.dd>
                  </div>
                  <div class={{options.wrappingClass}}>
                    <options.dt class="font-semibold mr-2">{{t
                        "pslf.employer_workflow.state"
                      }}:</options.dt>
                    <options.dd>{{this.formData.employerState}}</options.dd>
                  </div>
                  <div class={{options.wrappingClass}}>
                    <options.dt class="font-semibold mr-2">{{t
                        "pslf.employer_workflow.zip_code"
                      }}:</options.dt>
                    <options.dd>{{this.formData.employerPostalCode}}</options.dd>
                  </div>
                  <div class={{options.wrappingClass}}>
                    <options.dt class="font-semibold mr-2">{{t
                        "pslf.workflow.start_date"
                      }}:</options.dt>
                    <options.dd>{{safeFormatDate
                        this.formData.employmentStartDate
                        month="long"
                        day="2-digit"
                        year="numeric"
                        timeZone="UTC"
                      }}</options.dd>
                  </div>
                  {{#if this.employmentEndDate}}
                    <div class={{options.wrappingClass}}>
                      <options.dt class="font-semibold mr-2">{{t
                          "pslf.workflow.end_date"
                        }}:</options.dt>
                      <options.dd>{{safeFormatDate
                          this.employmentEndDate
                          month="long"
                          day="2-digit"
                          year="numeric"
                          timeZone="UTC"
                        }}</options.dd>
                    </div>
                  {{/if}}
                  <div class={{options.wrappingClass}}>
                    <options.dt class="font-semibold mr-2">{{t
                        "pslf.employer_workflow.average_hours_per_week"
                      }}:</options.dt>
                    <options.dd>{{this.formData.employmentHours}}</options.dd>
                  </div>
                  <div class={{options.wrappingClass}}>
                    <options.dt class="font-semibold mr-2">{{t
                        "pslf.employer_workflow.employment_type"
                      }}:</options.dt>
                    <options.dd>{{this.formData.employmentStatus}}</options.dd>
                  </div>
                  <div class={{options.wrappingClass}}>
                    <options.dt class="font-semibold mr-2">{{t
                        "pslf.employer_workflow.your_name"
                      }}:</options.dt>
                    <options.dd>{{this.formData.employerContactName}}</options.dd>
                  </div>
                  <div class={{options.wrappingClass}}>
                    <options.dt class="font-semibold mr-2">{{t
                        "pslf.employer_workflow.your_title"
                      }}:</options.dt>
                    <options.dd>{{this.formData.employerContactTitle}}</options.dd>
                  </div>
                  <div class={{options.wrappingClass}}>
                    <options.dt class="font-semibold mr-2">{{t
                        "pslf.employer_workflow.your_phone_number"
                      }}:</options.dt>
                    <options.dd>{{this.formData.employerContactPhoneNumber}}</options.dd>
                  </div>
                  <div class={{options.wrappingClass}}>
                    <options.dt class="font-semibold mr-2">{{t
                        "pslf.employer_workflow.your_email_address"
                      }}:</options.dt>
                    <options.dd>{{this.formData.employerContactEmail}}</options.dd>
                  </div>
                </DefinitionList>
              {{else}}
                <AlertBanner @intent="warning" class="my-4">
                  <p class="pt-1">
                    {{t "pslf.employer_workflow.warning" htmlSafe=true}}
                  </p>
                </AlertBanner>
                <SignaturePad
                  @onSignatureUpdate={{this.onSignatureUpdate}}
                  @errors={{this.signatureErrors}}
                />
              {{/if}}
            </VStack>
          </:body>
          <:footer>
            {{#if (eq this.modalStep "confirmation")}}
              <Footer
                @onCancel={{this.toggleConfirmationModal}}
                @onSubmit={{this.submitTask.perform}}
                @isRunning={{this.submitTask.isRunning}}
                @submitText={{t "common.next"}}
                @cancelText={{t "common.cancel"}}
              />
            {{else}}
              <Footer
                @onCancel={{this.goBackToConfirmation}}
                @onSubmit={{this.submitTask.perform}}
                @isRunning={{this.submitTask.isRunning}}
                @submitText={{t "sign"}}
                @cancelText={{t "common.back"}}
              />
            {{/if}}
          </:footer>
        </Modal>
      {{/if}}
    </VStack>
  </template>
}

export default PslfEmployerNonPartnerSign;
